import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { standupApi } from "@/lib/standup-api";
import DashboardSidebar from "@/components/DashboardSidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Sparkle, Diamond, CaretRight, Check, ArrowSquareOut, SlidersHorizontal } from "@phosphor-icons/react";
import { toast } from "sonner";

// StandUp Home — Frame 1.1 (Host tab) + 1.2 (Join tab).
//
// Two surfaces share one route so users get one "enter the room" mental model.
// Host path → posts to /standup → routes into /standup/new (setup wizard).
// Join path → lists invite-bound sessions in the 24h window → routes into
//   /standup/session/:id/launch (the loader) once one is selected.
//
// The setup wizard owns the full configuration; this surface is intentionally
// just a fork in the road so the homepage stays calm.
export default function StandUp() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [tab, setTab] = useState("host");

  useEffect(() => {
    if (!authLoading && !user) navigate("/login", { replace: true });
  }, [authLoading, user, navigate]);

  if (authLoading || !user) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  }

  return (
    <div className="min-h-screen flex bg-white" data-testid="standup-page">
      <DashboardSidebar
        collapsed={collapsed}
        onToggleCollapsed={() => setCollapsed((v) => !v)}
        user={user}
        onNewProject={() => navigate("/dashboard")}
        onOpenMilestones={() => navigate("/dashboard")}
        onOpenSupport={() => navigate("/dashboard")}
        activeItem="standup"
      />
      <main className="flex-1 relative bg-[radial-gradient(circle_at_50%_0%,#EAF1FF_0%,#FFFFFF_55%)] overflow-y-auto">
        {/* Manage Meetings pill — top right */}
        <div className="absolute top-6 right-8">
          <button
            type="button"
            onClick={() => navigate("/standup/manage")}
            className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white/80 backdrop-blur px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-white shadow-sm"
            data-testid="manage-meetings-btn"
          >
            <SlidersHorizontal size={13} weight="bold" />
            Manage Meetings
            <ArrowSquareOut size={11} weight="bold" />
          </button>
        </div>

        <div className="max-w-2xl mx-auto pt-24 px-6 pb-20">
          {/* Hero */}
          <div className="text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/70 border border-violet-100 text-slate-700 px-3 py-1 text-[11px] font-semibold tracking-wide">
              <Sparkle size={11} weight="fill" className="text-violet-500" />
              StandUp · Step 1
            </div>
            <h1 className="mt-5 text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">
              How are you joining <span className="text-sky-500">today's StandUp</span>
            </h1>
            <p className="mt-3 text-sm text-slate-500 max-w-md mx-auto">
              Set the tone for your morning sync — start one for your team or hop into a session that's already running.
            </p>
          </div>

          {/* Segmented tabs */}
          <div className="mt-10 flex items-center justify-center gap-2" role="tablist">
            <TabButton active={tab === "host"} onClick={() => setTab("host")} testid="tab-host">
              Host a Standup
            </TabButton>
            <TabButton active={tab === "join"} onClick={() => setTab("join")} testid="tab-join">
              Join a Standup
            </TabButton>
          </div>

          {/* Tab body */}
          <div className="mt-6">
            {tab === "host" ? <HostCard /> : <JoinCard />}
          </div>
        </div>
      </main>
    </div>
  );
}

function TabButton({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      data-testid={testid}
      className={`h-9 px-4 rounded-md text-sm font-semibold transition-colors ${
        active
          ? "bg-slate-950 text-white"
          : "bg-white text-slate-700 border border-slate-200 hover:bg-slate-50"
      }`}
    >
      {children}
    </button>
  );
}

function HostCard() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [cadence, setCadence] = useState("daily");
  const [submitting, setSubmitting] = useState(false);

  async function continueSetup() {
    if (!name.trim()) {
      toast.error("Give your standup a name first");
      return;
    }
    setSubmitting(true);
    // Stash the partially-filled state via query string so the wizard can
    // prefill Step 2. The actual create happens at Step 3 finish.
    const qs = new URLSearchParams({ name: name.trim(), cadence }).toString();
    navigate(`/standup/new?${qs}`);
  }

  return (
    <div
      className="mx-auto max-w-md rounded-xl border-2 border-violet-200 bg-white p-6 shadow-[0_8px_30px_-12px_rgba(124,58,237,0.25)]"
      data-testid="host-card"
    >
      <div className="flex items-start gap-3">
        <span className="relative grid place-items-center h-10 w-10 rounded-md bg-violet-100">
          <Sparkle size={18} weight="fill" className="text-violet-600" />
          <span className="absolute -top-1 -left-1 h-3 w-3 rounded-full ring-2 ring-white bg-violet-500" />
        </span>
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-bold text-slate-900">Host</h3>
            <span className="inline-flex items-center rounded bg-slate-900 text-white text-[9px] font-bold tracking-widest px-1.5 py-0.5">
              OWNER CONTROLS
            </span>
          </div>
          <p className="mt-0.5 text-xs text-slate-500">Run the room, set the cadence, invite your team keep things moving.</p>
        </div>
      </div>

      <div className="mt-5">
        <label className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">Meeting Name</label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Vortex morning sync"
          className="mt-1 h-9 text-sm"
          data-testid="meeting-name-input"
        />
      </div>

      <div className="mt-4">
        <label className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">Cadence</label>
        <div className="mt-1 grid grid-cols-3 gap-2">
          <CadenceCard label="Daily" sub="Mon - Fri 9:30am" value="daily" current={cadence} onSelect={setCadence} />
          <CadenceCard label="Weekly" sub="1x a week or more" value="weekly" current={cadence} onSelect={setCadence} />
          <CadenceCard label="Custom" sub="Choose recurrence" value="custom" current={cadence} onSelect={setCadence} />
        </div>
      </div>

      <Button
        onClick={continueSetup}
        disabled={submitting}
        className="mt-5 w-full h-11 bg-slate-950 hover:bg-slate-800 text-white font-semibold"
        data-testid="continue-setup-btn"
      >
        Continue Setup <CaretRight size={14} weight="bold" className="ml-1" />
      </Button>
    </div>
  );
}

function CadenceCard({ label, sub, value, current, onSelect }) {
  const active = current === value;
  return (
    <button
      type="button"
      onClick={() => onSelect(value)}
      className={`text-left rounded-md border px-3 py-2 transition-colors ${
        active
          ? "border-violet-500 bg-violet-50/60"
          : "border-slate-200 bg-white hover:border-slate-300"
      }`}
      data-testid={`cadence-${value}`}
    >
      <div className="flex items-center gap-1 text-xs font-semibold text-slate-900">
        {active && <Check size={11} weight="bold" className="text-violet-600" />}
        {label}
      </div>
      <div className="mt-0.5 text-[10px] text-slate-500">{sub}</div>
    </button>
  );
}

function JoinCard() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    standupApi.activeSessions().then((r) => {
      setSessions(r.data.sessions || []);
      if (r.data.sessions?.length) setSelectedId(r.data.sessions[0].session_id);
    }).catch(() => setSessions([]));
  }, []);

  function joinNow() {
    if (!selectedId) {
      toast.error("Pick a session first");
      return;
    }
    navigate(`/standup/session/${selectedId}/launch`);
  }

  const filtered = (sessions || []).filter((s) =>
    !filter || (s.standup_name || "").toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div
      className="mx-auto max-w-md rounded-xl border-2 border-pink-200 bg-white p-6 shadow-[0_8px_30px_-12px_rgba(236,72,153,0.2)]"
      data-testid="join-card"
    >
      <div className="flex items-start gap-3">
        <span className="relative grid place-items-center h-10 w-10 rounded-md bg-sky-100">
          <Diamond size={18} weight="fill" className="text-sky-600" />
          <span className="absolute -top-1 -left-1 h-3 w-3 rounded-full ring-2 ring-white bg-sky-500" />
        </span>
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-bold text-slate-900">Participant</h3>
            <span className="inline-flex items-center rounded bg-slate-900 text-white text-[9px] font-bold tracking-widest px-1.5 py-0.5">
              JOIN THE HUDDLE
            </span>
          </div>
          <p className="mt-0.5 text-xs text-slate-500">Drop into an active session. Share your update and stay in sync.</p>
        </div>
      </div>

      <div className="mt-5">
        <Select value="" onValueChange={() => {}}>
          <SelectTrigger className="h-10 w-full text-sm bg-white" data-testid="meeting-picker">
            <SelectValue placeholder="Choose a meeting standup" />
          </SelectTrigger>
          <SelectContent>
            <div className="px-2 py-1">
              <Input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Filter…"
                className="h-8 text-xs"
              />
            </div>
            {filtered.map((s) => (
              <SelectItem key={s.session_id} value={s.session_id} onClick={() => setSelectedId(s.session_id)}>
                {s.standup_name}
              </SelectItem>
            ))}
            {filtered.length === 0 && (
              <div className="px-2 py-2 text-xs text-slate-400">No active sessions in the next 24 hours.</div>
            )}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-4 rounded-lg border border-slate-200 bg-white">
        <div className="px-4 py-3 flex items-center justify-between border-b border-slate-100">
          <span className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">Active Sessions</span>
          <span className="text-[10px] text-slate-400">({filtered.length} Events)</span>
        </div>
        <div className="max-h-[260px] overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="px-4 py-6 text-center text-xs text-slate-400" data-testid="join-empty">
              You'll see sessions here when a host opens one in the next 24 hours.
            </div>
          ) : (
            filtered.map((s, i) => {
              const letter = String.fromCharCode(65 + (i % 26));
              const active = s.session_id === selectedId;
              return (
                <button
                  type="button"
                  key={s.session_id}
                  onClick={() => setSelectedId(s.session_id)}
                  className={`w-full px-3 py-2.5 flex items-center gap-3 text-left transition-colors ${
                    active ? "bg-sky-50/70" : "hover:bg-slate-50"
                  }`}
                  data-testid={`session-row-${s.session_id}`}
                >
                  <span
                    className={`h-9 w-9 rounded-md text-white flex items-center justify-center text-sm font-bold ${
                      active ? "ring-2 ring-sky-500" : ""
                    }`}
                    style={{ background: hashGradient(s.session_id) }}
                  >
                    {letter}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-900 truncate">{s.standup_name}</div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {(s.host_email || "").split("@")[0]} · {formatDate(s.scheduled_for)}
                    </div>
                  </div>
                  {active && (
                    <span className="grid place-items-center h-5 w-5 rounded-full bg-sky-500 text-white">
                      <Check size={11} weight="bold" />
                    </span>
                  )}
                </button>
              );
            })
          )}
        </div>
      </div>

      <Button
        onClick={joinNow}
        disabled={!selectedId}
        className="mt-4 w-full h-11 bg-slate-950 hover:bg-slate-800 text-white font-semibold"
        data-testid="join-now-btn"
      >
        Join Now <CaretRight size={14} weight="bold" className="ml-1" />
      </Button>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

function hashGradient(seed) {
  let h = 0;
  for (let i = 0; i < (seed || "").length; i++) h = (h * 31 + seed.charCodeAt(i)) & 0xfff;
  const a = h % 360;
  const b = (a + 60) % 360;
  return `linear-gradient(135deg, hsl(${a}, 70%, 55%), hsl(${b}, 65%, 60%))`;
}
